import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  GROESSE_SCHRITT,
  MAX_GROESSE,
  MAX_VERSATZ,
  MIN_GROESSE,
  rundeVersatz,
} from "../shared/anordnung.ts";
import { MAX_PERSONEN, MIN_PERSONEN, mengenstand, zaehlung } from "../shared/personen.ts";
import type { BildAusListe } from "./lib/api.ts";
import { mitMarken, reihenlayout, type Figur, type Marke } from "./lib/anordnung.ts";
import { sanftesScrollen } from "./lib/bewegung.ts";
import { ausschnitt } from "./lib/umriss.ts";
import { KURZ_ZU_KLEIN } from "./lib/vorschau.ts";
import { naechsteAnsicht, type Ansicht } from "./lib/zaehlpruefung.ts";
import Figurbild from "./Figurbild.tsx";
import { personenText } from "./Personenzahl.tsx";
import Rahmenvorschau from "./Rahmenvorschau.tsx";
import Zaehlpruefung from "./Zaehlpruefung.tsx";

/**
 * Schritt 4 aus `PROTOTYP-PLAN.md`: die eine Reihe mit Bodenlinie.
 *
 * Eine Reihe, waagerecht gescrollt, kein Umbruch. Drei eindimensionale Korrekturen je
 * Figur (ARCHITEKTUR.md 6.2):
 *
 * | Was | Wie |
 * |---|---|
 * | Reihenfolge | aufnehmen, dann eine **Lücke** antippen — nicht durch Ziehen |
 * | Größe | Regler in der Werkzeugleiste, unten verankert |
 * | Höhe | die Figur senkrecht schieben |
 *
 * **Warum Umsortieren nicht durch Ziehen geht:** senkrecht ziehen heißt Höhe, und
 * waagerecht ziehen ist das Scrollen der Leiste. Bliebe Umsortieren als dritte Bedeutung
 * derselben Geste, müsste das System aus der Richtung raten (6.2).
 *
 * **Bearbeiten und Vorschau sind zwei Ansichten** (6.2). Die Vorschau steht seit
 * Schritt 7 in `src/Rahmenvorschau.tsx` — Rahmen 80 × 25 cm, weißes Papier, Zoom. Sie
 * **ersetzt** die Leiste, statt neben ihr zu stehen: sonst hingen 120 Bilder gleichzeitig
 * in der Seite, und genau die Bildrate ist die Frage dieses Schrittes.
 *
 * Jeder Text ist deutsch, auch jeder Hinweis (`CLAUDE.md`).
 *
 * **Vor der Kundin heißt ein Element der Reihe „Bild", im Code weiter `Figur`** (Carolin,
 * 24.09.2026). Damit hat jede der drei Mengen ihr eigenes Wort und keines doppelt: ein
 * hochgeladenes **Foto**, ein ausgeschnittenes **Bild** in der Reihe, und die **Personen
 * oder Tiere**, die darauf zu sehen sind und den Preis tragen (`shared/personen.ts`).
 * „Figur" war Annas Wort von ihrer heutigen Produktseite („6 bis 15 Figuren"), stand aber
 * in der Zählzeile direkt unter der Personenzahl — zwei verschiedene Zahlen, und eine
 * davon hieß nach etwas, das die Kundin nicht gezählt hat. Ein Kommentar, der von einer
 * „Figur" spricht, meint deshalb den Code und nicht den Bildschirm.
 */

/** Höhe einer Figur bei Größe 1. Bei rund 130 px ist eine stehende Person gut zu
 *  erkennen, und auf 390 px passen mehrere nebeneinander — bei 60 Personen ist das der
 *  Unterschied zwischen Überblick und Scrollen. ⚠️ Gesetzt, nicht gemessen (`TODO.md`). */
export const KACHEL_HOEHE_PX = 132;

/** Ab wie vielen Pixeln eine senkrechte Bewegung als Schieben gilt und nicht als Tippen.
 *  Darunter ist es eine Auswahl — sonst verstellt jeder Fingertipp die Höhe. */
const ZIEH_SCHWELLE_PX = 6;

/**
 * Die feste Höhe des Streifens, in dem die Reihe liegt.
 *
 * ⚠️ **Fest und nicht mitwachsend**, und das ist der Punkt: die Bodenlinie ist der
 * Bezugspunkt, der die Regel ohne Text erklärt (ARCHITEKTUR.md 6.2, „Durchgezogen, immer
 * sichtbar"). Wächst der Streifen mit seinem Inhalt, wandert seine **Unterkante** nach
 * unten — die Linie also, und mit ihr alles darunter. Am 24.09.2026 gemessen: eine Figur
 * auf 200 % schob die Linie um eine ganze Kachelhöhe nach unten.
 *
 * Reserviert wird die größtmögliche Figur: doppelte Kachelhöhe (`MAX_GROESSE`) plus der
 * größtmögliche Hub (`MAX_VERSATZ`). Der Preis ist Luft über kleinen Figuren — der
 * Gegenwert ist eine Linie, die steht.
 */
const STREIFEN_HOEHE_PX = Math.ceil(KACHEL_HOEHE_PX * MAX_GROESSE * (1 + MAX_VERSATZ / 100));

interface Props {
  bilder: readonly BildAusListe[];
  /** `von` und `platz` zählen beide in der Reihe; `platz` ohne die aufgenommene Figur. */
  onAbsetzen: (von: number, platz: number) => void;
  onGroesse: (bildId: string, wert: number) => void;
  onVersatz: (bildId: string, wert: number) => void;
  /** Der Weg zu „Bild bearbeiten" (Schritt 5) — dort liegt auch der Weg zurück in den
   *  Zeichenbildschirm aus Schritt 3. */
  onZuordnung: (bildId: string) => void;
  /** Ein Bild aus der Reihe nehmen — Annas Wunsch vom 01.10.2026, siehe die Leiste unten. */
  onEntfernen: (bildId: string) => void;
}

/** Was während einer senkrechten Geste festgehalten wird. Steht in einer Referenz und
 *  nicht im Zustand: ein Zustandswechsel je Zeigerbewegung würde alle 60 Figuren neu
 *  rendern, und genau das ist der teure Fehler aus ARCHITEKTUR.md 3.1. */
interface Ziehen {
  bildId: string;
  element: HTMLElement;
  startY: number;
  startX: number;
  startVersatz: number;
  figurHoehe: number;
  begonnen: boolean;
  letzterVersatz: number;
}

export default function Reihe({
  bilder,
  onAbsetzen,
  onGroesse,
  onVersatz,
  onZuordnung,
  onEntfernen,
}: Props) {
  const [ausgewaehlt, setAusgewaehlt] = useState<string | null>(null);
  /** Für welches Bild steht die Rückfrage „wirklich entfernen?" in der Leiste? */
  const [fragtEntfernen, setFragtEntfernen] = useState<string | null>(null);
  // Wechselt die Auswahl, ist die Rückfrage hinfällig — sonst stünde sie beim Zurückkehren
  // zum ersten Bild unvermittelt wieder da. Angepasst während des Renderns statt in einem
  // Effekt (React: „Storing information from previous renders").
  if (fragtEntfernen !== null && fragtEntfernen !== ausgewaehlt) setFragtEntfernen(null);
  const [aufgenommen, setAufgenommen] = useState<string | null>(null);
  const [zeigtVorschau, setZeigtVorschau] = useState(false);
  /** Steht der Schluss-Check vor der Vorschau (`src/Zaehlpruefung.tsx`)? */
  const [zeigtPruefung, setZeigtPruefung] = useState(false);
  /**
   * Die Personensumme, für die zuletzt „Passt, weiter" getippt wurde — oder `null`.
   *
   * An der **Summe** und nicht an einem Ja/Nein: ändert jemand danach eine Zahl, stimmt
   * die Bestätigung nicht mehr, und der Check kommt wieder. Ein einmal gesetztes Flag
   * ließe genau den Fall durch, für den es den Check gibt.
   */
  const [geprueftBei, setGeprueftBei] = useState<number | null>(null);
  /**
   * Hat jemand in dieser Sitzung schon einmal umgestellt?
   *
   * Solange nicht, steht ein Satz in der Werkzeugleiste, der sagt, dass es geht (Annas
   * Test, 29.09.2026: sie hat es zuerst mit Ziehen versucht). Danach ist er weg — ein
   * Hinweis, der bleibt, nachdem er befolgt wurde, ist nur noch Höhe.
   */
  const [hatUmgestellt, setHatUmgestellt] = useState(false);
  const [werkzeugHoehe, setWerkzeugHoehe] = useState(0);
  const streifen = useRef<HTMLDivElement>(null);
  const werkzeugleiste = useRef<HTMLDivElement>(null);
  const zieht = useRef<Ziehen | null>(null);
  const gatter = useRef<number | null>(null);

  // Wie hoch die feste Werkzeugleiste gerade ist. Gemessen, weil sie mal zwei und mal
  // vier Zeilen hoch ist — ein geratener Abstand wäre mal zu groß, mal zu klein, und zu
  // klein hieße: der letzte Satz der Seite liegt unter der Leiste.
  useEffect(() => {
    const element = werkzeugleiste.current;
    if (!element) {
      setWerkzeugHoehe(0);
      return;
    }
    const messen = () => {
      setWerkzeugHoehe(element.getBoundingClientRect().height);
    };
    messen();
    const beobachter = new ResizeObserver(messen);
    beobachter.observe(element);
    return () => {
      beobachter.disconnect();
    };
  }, [aufgenommen, ausgewaehlt, zeigtVorschau, zeigtPruefung, hatUmgestellt]);

  const figuren = useMemo<Figur[]>(
    () =>
      bilder.map((bild) => ({
        bildId: bild.bildId,
        // Ohne Umriss ist die Figur noch nicht freigestellt — sie steht quadratisch in
        // der Reihe, bis sie umfahren ist, und die Warteschlange aus Schritt 3 holt sie
        // ohnehin.
        seitenverhaeltnis: bild.umriss
          ? ausschnitt(bild.umriss, bild.breite, bild.hoehe).seitenverhaeltnis
          : 1,
        groesse: bild.groesse,
        versatz: bild.versatz,
      })),
    [bilder],
  );

  const nachId = useMemo(() => new Map(bilder.map((bild) => [bild.bildId, bild])), [bilder]);
  const aufgenommenerIndex = figuren.findIndex((figur) => figur.bildId === aufgenommen);

  /** Beim Aufnehmen liegt die Reihe **ohne** die aufgenommene Figur da, mit Lücken
   *  dazwischen. Das ist der sichtbare Sprung, der erklärt, was zu tun ist. */
  const layout = useMemo(() => {
    if (aufgenommenerIndex === -1) {
      return { ...reihenlayout(figuren, KACHEL_HOEHE_PX), marken: [] as Marke[] };
    }
    const uebrige = figuren.filter((_, i) => i !== aufgenommenerIndex);
    return mitMarken(reihenlayout(uebrige, KACHEL_HOEHE_PX));
  }, [figuren, aufgenommenerIndex]);

  const gewaehlteFigur = layout.figuren.find((figur) => figur.bildId === ausgewaehlt);
  const gewaehltesBild = ausgewaehlt === null ? undefined : nachId.get(ausgewaehlt);
  const gewaehlteGroesse = figuren.find((figur) => figur.bildId === ausgewaehlt)?.groesse ?? 1;
  /** Die Figur, die gerade in der Hand liegt — sie steht nicht mehr in der Reihe. */
  const aufgenommeneFigur = figuren[aufgenommenerIndex];
  const aufgenommenesBild = aufgenommen === null ? undefined : nachId.get(aufgenommen);

  // Wird eine Figur ausgewählt, öffnet sich unten die feste Werkzeugleiste — und auf
  // einem 667 px hohen Bildschirm liegt die Bodenlinie danach hinter ihr. Also einmal
  // scrollen, sodass die Füße über der Leiste stehen. `scroll-margin-bottom` trägt die
  // gemessene Leistenhöhe; ohne sie schöbe der Browser die Linie exakt an den
  // Fensterrand, also hinter die Leiste.
  useEffect(() => {
    if (ausgewaehlt === null && aufgenommen === null) return;
    const element = streifen.current;
    if (!element) return;
    element.scrollIntoView({ block: "end", behavior: sanftesScrollen() });
  }, [ausgewaehlt, aufgenommen, werkzeugHoehe]);

  const versatzZeigen = useCallback((stand: Ziehen, versatz: number) => {
    stand.letzterVersatz = versatz;
    if (gatter.current !== null) return;
    // Ein Gatter über `requestAnimationFrame`: eine Zeigerbewegung je Bild, nicht je
    // Ereignis (ARCHITEKTUR.md 3.1). Angefasst wird **nur** das gezogene Element, über
    // seine Referenz — die anderen 59 rendern nicht neu.
    gatter.current = requestAnimationFrame(() => {
      gatter.current = null;
      const aktuell = zieht.current;
      if (!aktuell) return;
      const versatzPx =
        ((aktuell.letzterVersatz - aktuell.startVersatz) / 100) * aktuell.figurHoehe;
      aktuell.element.style.transform = `translateY(${String(-versatzPx)}px)`;
    });
  }, []);

  function zeigerRunter(event: React.PointerEvent<HTMLElement>, figur: Figur, hoehe: number) {
    if (aufgenommen !== null) return;
    zieht.current = {
      bildId: figur.bildId,
      element: event.currentTarget,
      startY: event.clientY,
      startX: event.clientX,
      startVersatz: figur.versatz,
      figurHoehe: hoehe,
      begonnen: false,
      letzterVersatz: figur.versatz,
    };
  }

  function zeigerBewegt(event: React.PointerEvent<HTMLElement>) {
    const stand = zieht.current;
    if (!stand) return;
    const dy = event.clientY - stand.startY;
    const dx = event.clientX - stand.startX;

    if (!stand.begonnen) {
      // Waagerecht gewinnt der Browser: das ist das Scrollen der Leiste, und
      // `touch-action: pan-x` gibt es ihm. Senkrecht erst ab der Schwelle, damit ein
      // Tippen eine Auswahl bleibt.
      if (Math.abs(dy) < ZIEH_SCHWELLE_PX || Math.abs(dy) <= Math.abs(dx)) return;
      stand.begonnen = true;
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        // Absichtlich still, wie in `Umfahren.tsx`: ohne Fangen schiebt es weiter, nur
        // verliert ein sehr schneller Finger die Figur am Rand.
      }
    }

    // Nach oben schieben hebt an. Der Bildschirm zählt nach unten, das Modell nach oben.
    const gezogen = stand.startVersatz + (-dy / stand.figurHoehe) * 100;
    versatzZeigen(stand, rundeVersatz(gezogen));
  }

  function zeigerHoch() {
    const stand = zieht.current;
    zieht.current = null;
    if (!stand) return;
    if (gatter.current !== null) {
      cancelAnimationFrame(gatter.current);
      gatter.current = null;
    }
    // Die Verschiebung von Hand wieder abnehmen: gleich kommt sie aus dem Zustand, und
    // beides zusammen wäre der doppelte Versatz.
    stand.element.style.transform = "";
    if (!stand.begonnen) {
      setAusgewaehlt(stand.bildId);
      return;
    }
    if (stand.letzterVersatz !== stand.startVersatz) {
      onVersatz(stand.bildId, stand.letzterVersatz);
    }
  }

  function zeigerAbgebrochen() {
    const stand = zieht.current;
    zieht.current = null;
    if (!stand) return;
    // Ein abgebrochener Zeiger — der Browser übernimmt das waagerechte Scrollen, ein
    // Anruf kommt herein — lässt die Figur, wo sie war. Etwas zu speichern, was niemand
    // beendet hat, wäre die schlechtere Richtung.
    stand.element.style.transform = "";
  }

  /**
   * Die beiden Zahlen, die dauerhaft dastehen (Schritt 5, `SPEC.md` „Person ≠
   * Bildelement"). Gerechnet in `shared/personen.ts`, damit dieselbe Summe später auf
   * dem Server steht und nicht zweimal woanders (6.9).
   */
  const gezaehlt = useMemo(() => zaehlung(bilder), [bilder]);
  const stand = mengenstand(gezaehlt.personen);
  const ansicht: Ansicht = zeigtPruefung ? "pruefung" : zeigtVorschau ? "vorschau" : "reihe";

  return (
    <section className="flex flex-col gap-3">
      {/* Die eine schmale Zeile aus dem Aufriss in ARCHITEKTUR.md 6.2 — „der Zähler ist
          die einzige Stelle, an der eine Zahl dauerhaft steht". Seit Schritt 5 trägt sie
          **beide** Zahlen getrennt, und sie steht auch in der Vorschau: das
          Abnahmekriterium verlangt sie „jederzeit". Deshalb ist der Titel „Vorschau" weg —
          welche Ansicht offen ist, sagt der Knopf.

          ⚠️ **Der Knopf steht seit dem 24.09.2026 in der zweiten Zeile und nicht neben der
          Überschrift.** „Personen oder Tiere" ist Annas eigene Formulierung (Carolins
          Entscheidung vom selben Tag) und damit zehn Zeichen länger; neben dem Knopf blieb
          für die Überschrift auf 390 px kein Platz mehr. Oben steht jetzt die Zahl allein,
          darunter die zweite Zahl und der Weg in die Vorschau. */}
      <div className="flex flex-col">
        <h2 className="text-lg font-medium text-neutral-900">
          {String(gezaehlt.personen)} von {String(MAX_PERSONEN)} Personen oder Tieren
        </h2>
        <div className="flex items-baseline justify-between gap-3">
          <p className={stand === "zu viel" ? "text-sm text-red-800" : "text-sm text-neutral-600"}>
            {anzahlBilder(gezaehlt.bildelemente)}
            {mengenhinweis(stand)}
          </p>
          {/* Der Weg in die Vorschau führt seit dem 29.09.2026 über den Schluss-Check,
              solange die Zählung für diesen Stand nicht bestätigt ist. Er steht **hier**
              und nicht am Ende der Bestellstrecke, weil es im Prototyp keine gibt — und
              weil das Ansehen der ganzen Reihe ohnehin der Augenblick ist, in dem jemand
              prüft (`src/Zaehlpruefung.tsx`). */}
          <button
            type="button"
            onClick={() => {
              setAufgenommen(null);
              // Die Regel steht in `src/lib/zaehlpruefung.ts`, damit ein Test sie lesen
              // kann, ohne eine Oberfläche zu rendern — das Projekt hat kein jsdom.
              const naechste = naechsteAnsicht(ansicht, gezaehlt, geprueftBei);
              setZeigtPruefung(naechste === "pruefung");
              setZeigtVorschau(naechste === "vorschau");
            }}
            className="min-h-11 shrink-0 rounded-lg px-3 text-sm font-medium text-neutral-900 underline"
          >
            {zeigtVorschau || zeigtPruefung ? "Weiter bearbeiten" : "Ganze Reihe ansehen"}
          </button>
        </div>
      </div>

      {zeigtPruefung ? (
        <Zaehlpruefung
          bilder={bilder}
          onWeiter={() => {
            setGeprueftBei(gezaehlt.personen);
            setZeigtPruefung(false);
            setZeigtVorschau(true);
          }}
          onZurueck={() => {
            setZeigtPruefung(false);
          }}
        />
      ) : zeigtVorschau ? (
        /* Schritt 7: der Rahmen 80 × 25 cm auf weißem Papier, mit Zoom. Bis zum
           24.09.2026 stand hier die ungerahmte Reihe; was daran zu wenig war, steht in
           `src/Rahmenvorschau.tsx`. */
        <Rahmenvorschau bilder={bilder} layout={layout} />
      ) : (
        <>
          {/* Waagerecht gescrollt, nie umgebrochen (6.2). `touch-action: pan-x` gibt dem
              Browser genau eine Richtung und lässt die senkrechte Geste bei uns;
              `overscroll-behavior` hält das Wischen in der Leiste, statt die Seite
              mitzunehmen. Das ist die Fallenklasse aus 2.2 — hier steht sie an einem Ort. */}
          <div
            ref={streifen}
            className="flex items-end overflow-x-auto overflow-y-hidden overscroll-x-contain"
            // ⚠️ **Feste Höhe, unten ausgerichtet** — vom `usability-reviewer` am
            // 24.09.2026 gemessen: vorher wuchs die Leiste nach **unten**, weil ihre
            // Oberkante im Fluss feststeht. Eine Figur auf 200 % schob die Bodenlinie um
            // eine ganze Kachelhöhe nach unten, und mit ihr die Werkzeugleiste — auf
            // einem iPhone SE aus dem Bild heraus. Die Bodenlinie ist aber der
            // Bezugspunkt, der die Regel ohne Text erklärt (6.2); sie darf sich nicht
            // bewegen, nur weil eine Figur größer wird.
            style={{
              touchAction: "pan-x",
              height: STREIFEN_HOEHE_PX,
              // Abstand nach unten beim Hereinscrollen: genau die Leiste, die dort liegt.
              scrollMarginBottom: werkzeugHoehe + 8,
            }}
          >
            <div
              // ⚠️ **`shrink-0` ist hier keine Kosmetik, sondern der Unterschied zwischen
              // einer scrollenden und einer abgeschnittenen Reihe** (24.09.2026, vom
              // `usability-reviewer` gefunden und unabhängig nachgemessen). Diese Fläche
              // ist das einzige Kind eines Flex-Behälters. Ihre Breite steht unten in
              // `style.width` — aber ein Flex-Element darf schrumpfen, und weil es
              // `overflow-hidden` trägt, ist seine automatische Mindestbreite **0** statt
              // der Inhaltsbreite (CSS Flexbox, `min-width: auto`). Es schrumpfte also
              // exakt auf die Behälterbreite: gemessen 358 px, obwohl `style.width`
              // 785,5 px sagte. Damit war `scrollWidth === clientWidth`, der waagerechte
              // Wisch hatte nichts zu scrollen, und jede Figur jenseits der Kante war
              // **unerreichbar** — ohne Fehlermeldung.
              //
              // Der Zweig für die Vorschau oben trägt dieselbe Klassenliste und braucht
              // das **nicht**: dort ist die Fläche kein Flex-Element.
              className="relative shrink-0 overflow-hidden border-b-2 border-amber-400"
              // Die Bodenlinie ist die Unterkante dieser Fläche: eine gesenkte Figur
              // wird hier abgeschnitten, und genau das ist der Zweck des Versatzes —
              // die angeschnittenen Beine verschwinden unter der Linie.
              //
              // Die zwei zusätzlichen Pixel sind der untere Rand: mit `box-sizing:
              // border-box` gingen sie sonst von der Innenhöhe ab, und jede Figur wäre
              // oben um zwei Pixel beschnitten — genau das hat den Auswahlring
              // unsichtbar gemacht.
              style={{ width: Math.max(layout.breite, 1), height: layout.hoehe + 2 }}
            >
              {layout.marken.map((marke) => (
                <button
                  key={`marke-${String(marke.platz)}`}
                  type="button"
                  onClick={() => {
                    if (aufgenommenerIndex === -1) return;
                    onAbsetzen(aufgenommenerIndex, marke.platz);
                    setAufgenommen(null);
                    // Ab hier ist der Satz in der Leiste erledigt: wer einmal abgesetzt
                    // hat, weiß, wie es geht.
                    setHatUmgestellt(true);
                  }}
                  aria-label={`Hier absetzen, Platz ${String(marke.platz + 1)}`}
                  className="absolute bottom-0 flex items-end justify-center rounded-lg border-2 border-dashed border-neutral-400 bg-neutral-50"
                  style={{ left: marke.links, width: marke.breite, height: layout.hoehe }}
                >
                  <span className="pb-2 text-xl text-neutral-500" aria-hidden="true">
                    ↓
                  </span>
                </button>
              ))}

              {layout.figuren.map((gelegt, i) => {
                const bild = nachId.get(gelegt.bildId);
                const figur = figuren.find((kandidat) => kandidat.bildId === gelegt.bildId);
                if (!bild || !figur) return null;
                return (
                  <Kachel
                    key={gelegt.bildId}
                    bild={bild}
                    links={gelegt.links}
                    breite={gelegt.breite}
                    hoehe={gelegt.hoehe}
                    hebung={gelegt.hebung}
                    // Beim Aufnehmen ist die Reihe ohne die aufgenommene Figur gelegt;
                    // die Zählung „Figur 3" muss trotzdem stimmen.
                    nummer={i + 1}
                    ausgewaehlt={gelegt.bildId === ausgewaehlt}
                    onPointerDown={(event) => {
                      zeigerRunter(event, figur, gelegt.hoehe);
                    }}
                    onPointerMove={zeigerBewegt}
                    onPointerUp={zeigerHoch}
                    onPointerCancel={zeigerAbgebrochen}
                  />
                );
              })}
            </div>
          </div>

          {/* Der Platzhalter unter der Leiste: die Werkzeuge liegen **fest am unteren
              Rand** (6.2, „Werkzeuge sitzen unten, im Daumenbereich"), und ohne diesen
              Abstand verdeckten sie das Ende der Seite. Die Höhe wird gemessen, nicht
              geraten — die Leiste ist mal zwei, mal vier Zeilen hoch. */}
          <div aria-hidden="true" style={{ height: werkzeugHoehe }} />

          {/* Werkzeuge erscheinen bei Auswahl und verschwinden wieder (6.2, Regel 1).
              Dauerhaft steht nur die Zeile darüber.

              ⚠️ **Fest am unteren Rand seit dem 24.09.2026.** Der `usability-reviewer`
              hat gemessen, dass der „Fertig"-Knopf auf einem iPhone SE aus dem Bild
              rutschte, sobald eine Figur größer wurde. Eine Werkzeugleiste, die man
              suchen muss, ist auf einem Telefon keine. */}
          <div
            ref={werkzeugleiste}
            className="fixed inset-x-0 bottom-0 z-10 mx-auto max-w-md border-t border-neutral-300 bg-white px-4 pt-3"
            style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
          >
            {aufgenommen !== null ? (
              <div className="flex flex-col gap-2 rounded-lg bg-neutral-100 px-4 py-3">
                <div className="flex items-center gap-3">
                  {/* ⚠️ Am 24.09.2026 im Bildschirmfoto gesehen: die aufgenommene Figur war
                    **nirgends** zu sehen — sie ist ja aus der Reihe genommen. „Wohin soll
                    die Figur?" fragt dann nach etwas, das niemand vor sich hat. Also
                    liegt sie jetzt sichtbar in der Hand. */}
                  {aufgenommenesBild && (
                    <span
                      className="block h-16 shrink-0"
                      style={{ width: 16 * (aufgenommeneFigur?.seitenverhaeltnis ?? 1) * 4 }}
                    >
                      <Figurbild bild={aufgenommenesBild} />
                    </span>
                  )}
                  <p aria-live="polite" className="text-sm text-neutral-800">
                    Dieses Bild hast du in der Hand. Tippe auf eine Lücke in der Reihe, dort wird es
                    abgesetzt.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setAufgenommen(null);
                  }}
                  className="min-h-14 self-start rounded-xl border border-neutral-900 px-5 text-base font-medium text-neutral-900"
                >
                  Zurücklegen
                </button>
              </div>
            ) : gewaehltesBild && gewaehlteFigur ? (
              <div className="flex flex-col gap-3 rounded-lg bg-neutral-100 px-4 py-3">
                <label className="flex flex-col gap-1 text-sm text-neutral-800">
                  {/* Der Regler ist zugleich die tastaturtaugliche Form (3.8): Pfeiltasten
                      bewegen ihn, ohne dass es eine zweite Bedienung braucht. */}
                  {/* In Prozent und nicht in Pixeln: „Größe 132 px" stand hier bis zum
                      24.09.2026 und ist eine Zahl für Entwickler. Vor der Kundin steht,
                      wie viel größer oder kleiner die Figur ist als die anderen. */}
                  Größe {String(Math.round(gewaehlteGroesse * 100))} %
                  <input
                    type="range"
                    min={MIN_GROESSE}
                    max={MAX_GROESSE}
                    step={GROESSE_SCHRITT}
                    value={gewaehlteGroesse}
                    onChange={(event) => {
                      onGroesse(gewaehltesBild.bildId, Number(event.target.value));
                    }}
                    className="min-h-11"
                  />
                </label>
                {/* Ein Satz statt dreier Zeilen: die Leiste liegt fest am unteren Rand,
                      und in der langen Fassung verdeckte sie auf einem 667 px hohen
                      Bildschirm die Bodenlinie (gemessen am 24.09.2026). */}
                {/* ⚠️ **Löschen steht seit dem 01.10.2026 auch hier** — Annas Mail: sie hat
                      „relativ lange gesucht", bis sie den Weg über „Bild bearbeiten" fand. Die
                      Rückfrage tauscht Satz und Knopfreihe gegen eine gleich gebaute Zeile
                      aus, statt etwas darüber zu legen. Der Satz der Rückfrage reserviert
                      zwei Zeilen wie der Satz, den er ersetzt — sonst rutschte der Regler
                      darüber um 20 px (gemessen am 05.10.2026 bei 390 px). Die Bodenlinie
                      bleibt in beiden Zuständen bei y≈413. Der Mülleimer ist ein schmaler
                      vierter Knopf in **derselben** Zeile, keine neue Zeile. */}
                {fragtEntfernen === gewaehltesBild.bildId ? (
                  <>
                    <p role="alert" className="min-h-10 text-sm text-neutral-800">
                      Bild wirklich entfernen? Zurückholen geht nicht.
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setFragtEntfernen(null);
                        }}
                        className="min-h-14 flex-1 rounded-xl border border-neutral-900 px-3 text-sm font-medium text-neutral-900"
                      >
                        Behalten
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const bildId = gewaehltesBild.bildId;
                          setFragtEntfernen(null);
                          setAusgewaehlt(null);
                          onEntfernen(bildId);
                        }}
                        className="min-h-14 flex-1 rounded-xl bg-red-700 px-3 text-sm font-medium text-white"
                      >
                        Ja, entfernen
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-neutral-600">
                      Schwebt es oder fehlen die Beine? Bild senkrecht schieben.
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setAufgenommen(gewaehltesBild.bildId);
                        }}
                        className="min-h-14 flex-1 rounded-xl bg-neutral-900 px-2 text-sm font-medium text-white"
                      >
                        {/* Hieß bis zum 01.10.2026 „Aufnehmen" — Anna: „im ersten Moment nicht
                        ganz eindeutig", ihr Vorschlag war „Versetzen". */}
                        Versetzen
                      </button>
                      {/* Seit Schritt 5 führt dieser Knopf nicht mehr direkt ins Zeichnen,
                        sondern auf den Bildschirm mit dem Foto (`src/Zuordnung.tsx`) —
                        dort stehen Personenzahl, Name **und** der Weg ins Umfahren. Eine
                        vierte Zeile in dieser Leiste hätte die Bodenlinie wieder aus dem
                        Bild geschoben; das war am 24.09.2026 der teure Fund.

                        ⚠️ **Der Knopf hieß bis zum 29.09.2026 „Wer ist das?" und heißt
                        bis zum 07.10.2026 „Bild ändern"** — aus Annas Test an diesem Tag. Sie hatte
                        einen Umriss gezogen, der nicht saß, und fand den Weg zurück ins
                        Umfahren nicht: er liegt hinter diesem Knopf, aber „Wer ist das?"
                        verspricht eine Person und keine Geometrie. Der Bildschirm
                        dahinter trägt beides, also nennt der Knopf jetzt beides — und
                        die Leiste bleibt bei drei Knöpfen, die Bodenlinie also da, wo
                        sie am 24.09. hingemessen wurde.

                        **Seit dem 07.10.2026 „Bild bearbeiten"** — Annas Testpersonen
                        (Eltern, Partner) dachten bei „Bild ändern", sie ersetzen damit das
                        ganze Foto. Ihr Vorschlag. */}
                      <button
                        type="button"
                        onClick={() => {
                          onZuordnung(gewaehltesBild.bildId);
                        }}
                        className="min-h-14 flex-1 rounded-xl border border-neutral-900 px-2 text-sm font-medium text-neutral-900"
                      >
                        Bild bearbeiten
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setAusgewaehlt(null);
                        }}
                        className="min-h-14 rounded-xl px-2 text-sm font-medium text-neutral-700 underline"
                      >
                        Fertig
                      </button>
                      <button
                        type="button"
                        aria-label="Bild entfernen"
                        onClick={() => {
                          setFragtEntfernen(gewaehltesBild.bildId);
                        }}
                        className="flex min-h-14 w-12 shrink-0 items-center justify-center rounded-xl border border-red-700 text-red-700"
                      >
                        <svg
                          aria-hidden="true"
                          viewBox="0 0 24 24"
                          className="h-6 w-6"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14M10 11v6M14 11v6" />
                        </svg>
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              /* Nichts ausgewählt, nichts in der Hand: hier steht der Satz, der sagt, dass
                 man ein Bild antippen kann (Annas Test, 29.09.2026 — sie hat es zuerst mit
                 Ziehen versucht und die Lösung erst danach gefunden).

                 ⚠️ **Der Platz ist der Grund, warum er genau hier steht.** Die Leiste liegt
                 ohnehin fest am unteren Rand, und der Platzhalter darüber trägt ihre
                 **gemessene** Höhe — ein Satz an dieser Stelle schiebt also nichts aus dem
                 Bild, sondern füllt Raum, der schon reserviert ist. Über der Reihe hätte er
                 die Bodenlinie nach unten gedrückt, und die ist der Bezugspunkt, der die
                 Regel ohne Text erklärt (24.09.2026 gemessen, 6.2).

                 Weg ist er, sobald einmal umgestellt wurde — und bei einem einzigen Bild
                 kommt er gar nicht erst, weil es dann nichts umzustellen gibt. */
              !hatUmgestellt &&
              bilder.length > 1 && (
                <p className="px-1 text-sm text-neutral-700">
                  Tippe ein Bild an — dann kannst du es versetzen, in der Größe ändern oder
                  entfernen.
                </p>
              )
            )}
          </div>
        </>
      )}
    </section>
  );
}

interface KachelProps {
  bild: BildAusListe;
  links: number;
  breite: number;
  hoehe: number;
  hebung: number;
  nummer: number;
  ausgewaehlt: boolean;
  onPointerDown: (event: React.PointerEvent<HTMLElement>) => void;
  onPointerMove: (event: React.PointerEvent<HTMLElement>) => void;
  onPointerUp: () => void;
  onPointerCancel: () => void;
}

/**
 * Eine Figur in der Reihe.
 *
 * `memo`, weil bei 60 Figuren jede Größenänderung sonst 60 Kacheln neu rendert — der
 * gemessene Grund, warum sich `wizard-flow` bei mehr als 10 Elementen zäh anfühlen würde
 * (ARCHITEKTUR.md 3.1).
 */
const Kachel = memo(function Kachel({
  bild,
  links,
  breite,
  hoehe,
  hebung,
  nummer,
  ausgewaehlt,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerCancel,
}: KachelProps) {
  return (
    <button
      type="button"
      // Seit Schritt 5 steht hier, **wen** die Figur zeigt. Ohne das nennt der
      // Bildschirmleser bei sechzig Figuren sechzigmal dieselbe Zahlenreihe, und die
      // Zuordnung Bild ↔ Person aus AB § 1.3 gäbe es nur für Sehende.
      aria-label={`Bild ${String(nummer)} auswählen: ${wer(bild)}${
        bild.zuKlein ? `, ${KURZ_ZU_KLEIN}` : ""
      }`}
      aria-pressed={ausgewaehlt}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      // `ring-inset`: der Ring wird **nach innen** gezeichnet. Nach außen wüchse er
      // über die Kante des Streifens hinaus, und der schneidet ab (`overflow-hidden`) —
      // am 24.09.2026 gemessen: von vier Seiten blieben drei unsichtbar, und damit war
      // bei sechzig ähnlichen Fotos nicht mehr zu sehen, welche Figur ausgewählt ist.
      className={`absolute block ${ausgewaehlt ? "ring-4 ring-neutral-900 ring-inset" : ""}`}
      style={{
        left: links,
        bottom: hebung,
        width: breite,
        height: hoehe,
        // Waagerecht scrollt der Browser, senkrecht schieben wir. Eine Geste, eine
        // Bedeutung (6.2).
        touchAction: "pan-x",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      <Figurbild bild={bild} />
      {/* Die Zahl steht nur bei mehr als einer Person da. Eine „1" an jeder Figur wäre
          sechzigmal dieselbe Auskunft; erklärungsbedürftig ist allein der Fall, in dem
          eine Figur für mehrere Personen zählt (`SPEC.md`, „Person ≠ Bildelement").
          ⚠️ Bei sehr schmalen Figuren — gemessen wurden 17 px — deckt das Schild einen
          guten Teil der Figur ab. Das steht in `TODO.md`; die Ursache ist die
          Kachelhöhe, nicht das Schild. */}
      {bild.personen > 1 && (
        <span
          aria-hidden="true"
          className="absolute top-0 left-0 rounded-br-lg bg-neutral-900/80 px-1.5 text-[11px] leading-5 font-medium text-white"
        >
          {String(bild.personen)}
        </span>
      )}
      {/* Der Auflösungshinweis, seit dem 29.09.2026 **am Bild** statt als Kasten über der
          Seite. Nur ein Zeichen, kein Text: eine Figur ist in der Reihe im Mittel 47 px
          breit (gemessen am 24.09.2026), darunter passt kein Satz — nicht einmal der
          kurze, der für eine 110-px-Kachel geschrieben wurde (`KURZ_ZU_KLEIN`). Der steht
          deshalb im `aria-label` oben, und der ganze Satz auf „Bild bearbeiten", wo das Foto
          groß liegt.

          **Oben rechts**, gegenüber der Personenzahl: die beiden dürfen sich nicht
          verdecken, und bei sehr schmalen Figuren tun sie es fast. Das ist derselbe
          Engpass, der für das Personenschild schon in `TODO.md` steht — die Ursache ist
          die Kachelhöhe, nicht das Schild. */}
      {bild.zuKlein && (
        <span
          aria-hidden="true"
          className="absolute top-0 right-0 rounded-bl-lg bg-amber-400 px-1.5 text-[11px] leading-5 font-bold text-neutral-900"
        >
          !
        </span>
      )}
    </button>
  );
});

/** Wer auf einer Figur zu sehen ist, in einem Satzteil — für Bildschirmleser und für
 *  jede Stelle, die es schreiben muss. Ohne Beschriftung bleibt die Zahl. */
function wer(bild: BildAusListe): string {
  const gezaehlt = personenText(bild.personen);
  return bild.beschriftung === "" ? gezaehlt : `${bild.beschriftung}, ${gezaehlt}`;
}

/**
 * Der Zusatz in der Zählzeile, wenn die Menge außerhalb liegt.
 *
 * Die Grenze selbst steht in `shared/personen.ts` (6.9) — hier steht nur der deutsche
 * Satz dazu. ⚠️ Die 15 sind nicht bestätigt: Annas Shop nennt „6 bis 15 Figuren"
 * (`TODO.md`). Steht die Zahl falsch, ist es eine Konstante, kein Umbau.
 */
function mengenhinweis(stand: ReturnType<typeof mengenstand>): string {
  // Ohne die Einheit: die steht in der Überschrift direkt darüber, und in dieser Zeile
  // steht daneben noch der Knopf in die Vorschau.
  if (stand === "zu wenig") return ` · ab ${String(MIN_PERSONEN)} bestellbar`;
  if (stand === "zu viel") return ` · mehr als ${String(MAX_PERSONEN)} passen nicht`;
  return "";
}

/** „1 Bilder" wäre derselbe Fehler, den `anzahlFotos` in `src/App.tsx` am 24.09.2026
 *  hatte — deutsche Texte stehen vor einer Kundin, auch die aus einer Zählung.
 *
 *  „Bilder" und nicht „Figuren": vor der Kundin heißt ein Element der Reihe **Bild**
 *  (Carolin, 24.09.2026, siehe Dateikopf). „Personen" war hier nicht zu haben — die
 *  Überschrift direkt darüber zählt Personen, und zwei verschiedene Zahlen mit demselben
 *  Wort untereinander sind keine Auskunft, sondern ein Widerspruch. */
function anzahlBilder(anzahl: number): string {
  return anzahl === 1 ? "1 Bild" : `${String(anzahl)} Bilder`;
}
